import useTranslation from '@app/hooks/use-translation';
import {
  useConfigureADSyncMutation,
  useDisableADSyncMutation,
  useEnableADSyncMutation,
  useForceADSyncMutation,
  useLazyGetADSyncInfoQuery,
} from '@app/store/api/user-synchronization-AD.api';
import { Box, Button, CircleButton, CustomLabel, Input, Loading, Toggle } from '@ui';
import React, { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
type FormType = {
  period: number;
  filter: { key: string; value: string }[];
};

const UsersSynchronizationAD = () => {
  const { t } = useTranslation('pages.usersPage.AD_sync');
  const location = useLocation();
  const [getInfo, { isLoading, currentData: data }] = useLazyGetADSyncInfoQuery();

  const [forceSync] = useForceADSyncMutation();
  const [disableSync] = useDisableADSyncMutation();
  const [enableSync] = useEnableADSyncMutation();
  const [configSync, { isLoading: configLoading }] = useConfigureADSyncMutation();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: { filter: [], period: 1 },
  });

  useEffect(() => {
    getInfo()
      .unwrap()
      .then((data) => {
        const filter: FormType['filter'] = [];

        for (let i = 0; i < (data?.filter?.length || 0); i++) {
          const draft = Object.entries(data?.filter?.[i] || []);
          filter.push({
            key: draft[0][0],
            value: draft[0][1] as string,
          });
        }
        reset({ period: data?.auto_sync.period, filter });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getInfo, location.key]);

  const { fields, append, remove } = useFieldArray({ control, name: 'filter' });

  function handleAppendNewField() {
    append({ key: '', value: '' });
  }
  function handleRemoveField(index: number) {
    remove(index);
  }

  function onSubmit(data: FormType) {
    const formattedFilter = data.filter.map((filter) => ({ [filter.key]: filter.value }));
    configSync({ filter: formattedFilter, period: data.period });
  }

  function handleForceADSync() {
    forceSync();
  }

  function handleChangePeriodSync(status: boolean) {
    if (status) {
      enableSync();
      return;
    }
    disableSync();
  }

  if (isLoading) return <Loading />;
  return (
    <div className="pb-[20px]">
      <div className="py-[30px]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-[10px]">
            <div className="flex items-center gap-[10px] h-[30px]">
              <span className="text-[15px] text-3color font-medium">{t('period_sync')}</span>
              <Toggle
                checked={data?.auto_sync.enabled}
                onChecked={handleChangePeriodSync}
                size="large"
              />
            </div>
          </div>
          <Button
            className="!px-0"
            onClick={handleForceADSync}
            fill="linked"
            variant="danger"
            label={t('forced_sync')}
          />
        </div>
      </div>
      <Box className="p-[40px_40px_0px_40px]">
        <h3 className="text-[20px] text-0color font-bold ">{t('config')}</h3>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[10px] relative">
          <div className="flex items-center justify-end w-full gap-[10px]">
            <CustomLabel label={t('period_sync_input_label')} />
            <div className="w-[150px]">
              <Input
                {...register(`period`, {
                  valueAsNumber: true,
                  max: { value: 24, message: ' ' },
                  min: { value: 1, message: ' ' },
                })}
                type="number"
                error={errors.period?.message}
              />
            </div>
          </div>
          <div className="flex flex-col gap-[20px]">
            {fields.map(({ id }, index) => (
              <div key={id} className={`flex flex-col relative gap-[20px]`}>
                <div className="flex items-center gap-[20px]">
                  <div className="w-[300px]">
                    <Input
                      label={t('name')}
                      {...register(`filter.${index}.key`, {
                        required: { value: true, message: ' ' },
                      })}
                      error={errors.filter?.[index]?.key?.message}
                    />
                  </div>
                  <div className="w-[300px]">
                    <Input
                      label={t('value')}
                      {...register(`filter.${index}.value`, {
                        required: { value: true, message: ' ' },
                      })}
                      error={errors.filter?.[index]?.value?.message}
                    />
                  </div>
                </div>
                {/* <div className="flex flex-col gap-[6px]">
                  <CustomLabel label={t('value_label')} />
                  <div className="grid grid-rows-1 items-center grid-cols-[400px_500px] gap-x-[20px]">
                    <FormTabSwitcher
                      valuesList={valuesListSwitcher}
                      isActive={watch(`filter.${index}.switcher`)}
                      onChange={(value) => handleChangeSwitcher(value, index)}
                    />
                    <Select
                      placeholder={t('choose')}
                      defaultValue={watch(`filter.${index}.value`)}
                      onChange={(value) => setValue(`filter.${index}.value`, String(value))}
                      options={optionsObject[watch(`filter.${index}.switcher`)]}
                    />
                  </div> */}
                {/* </div> */}
                <div className="absolute -left-[30px] top-[26px] ">
                  <CircleButton
                    onClick={() => handleRemoveField(index)}
                    size={16}
                    className="text-dangerBg"
                    icon="TrashIcon"
                  />
                </div>
                {fields.length > 1 && index !== fields.length - 1 && (
                  <div className="border-b-solid border-b-[0.5px] border-b-3color py-[10px]" />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end w-full py-2">
            <Button
              label={t('add_config_elem')}
              onClick={handleAppendNewField}
              disabled={fields.length === 10}
              fill="linked"
              icon="PlusIcon"
            />
          </div>

          <div className="flex items-center py-4  sticky bottom-0 bg-white">
            <Button disabled={configLoading} type="submit" label={t('save_config')} />
          </div>
        </form>
      </Box>
    </div>
  );
};

export default UsersSynchronizationAD;
